/** The quick actions every value on a notification surface offers — shared by
 *  the notification editor and its panels, the ruleset editor, the timeline,
 *  the anatomy viewer and the design-import review, so the same reference gets
 *  the same set wherever it appears.
 *
 *  Every href comes from `@/utils/gridLinks`; every action from `act.*`.
 *
 *  The one id-space fact this family rests on: **`?edit=<id>` intents exist
 *  ONLY on `/notifications/blocks`, `/themes`, `/block-lists` and
 *  `/language-policies`** — those four dialogs are the whole set. Everything
 *  else here is either a real detail route or a `?q=` filter.
 *
 *  Blocks, themes and language policies are shared across operators (a system
 *  row is referenced by many tenants), so "which notifications use this?"
 *  searches EVERY tenant — scoping those to the notification you happen to be
 *  reading would hide exactly the answer the question is asking for. Values
 *  that are tenant-owned (roles, tours, events) keep the `?tenant=` scope.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  bookingsHref,
  cond,
  createdWindowNode,
  deliveryLogsHref,
  featureFlagsHref,
  findReplaceHref,
  idCond,
  notificationsHref,
  tagsHref,
  toursHref,
} from '@/utils/gridLinks';

export interface NotificationCtx {
  /** Scopes the tenant-owned grid links (`?tenant=`). Null = system / unknown. */
  tenantId?: number | null | undefined;
}

const tenantOf = (ctx: NotificationCtx = {}) => ctx.tenantId ?? null;

/** 30-day window for the "fires in the last 30 days" links (the timeline's
 *  stats window), instead of the delivery-log grid's default 7 days. */
const THIRTY_DAYS = () => createdWindowNode(30);

// ---- The four `?edit=` dialogs --------------------------------------------

export interface CodenamedRef {
  id: number;
  codename?: string | null;
}

export type CodenameNamespace = 'theme' | 'language_policy' | 'block' | 'list';

const CODENAME_LABELS: Record<CodenameNamespace, string> = {
  theme: 'theme',
  language_policy: 'language policy',
  block: 'block',
  list: 'block list',
};

/** "Which notifications reference this codename?" — the answer for a value
 *  that has a codename but no id yet (a design-import draft, a payload that
 *  names a block by codename only). */
export function codenameUsageActions(
  namespace: CodenameNamespace,
  codename: string,
): ValueAction[] {
  return [
    act.filter(
      notificationsHref([cond(namespace, codename)]),
      `Notifications using this ${CODENAME_LABELS[namespace]}`,
    ),
    act.copy(codename, 'Copy codename'),
  ];
}

/** `/notifications/<kind>?edit=<id>` + "which notifications reference it". */
function dialogActions(
  path: string,
  namespace: CodenameNamespace,
  label: string,
  ref: CodenamedRef,
): ValueAction[] {
  return [
    act.open(`${path}?edit=${ref.id}`, `Open ${label}`),
    ...(ref.codename ? codenameUsageActions(namespace, ref.codename) : []),
  ];
}

export const languagePolicyActions = (p: CodenamedRef): ValueAction[] =>
  dialogActions('/notifications/language-policies', 'language_policy', 'language policy', p);

export const themeActions = (t: CodenamedRef): ValueAction[] =>
  dialogActions('/notifications/themes', 'theme', 'theme', t);

export const blockActions = (b: CodenamedRef): ValueAction[] =>
  dialogActions('/notifications/blocks', 'block', 'block', b);

export const blockListActions = (l: CodenamedRef): ValueAction[] =>
  dialogActions('/notifications/block-lists', 'list', 'block list', l);

// ---- Targeting ------------------------------------------------------------

/** A staff role: the bookings it triggers on, and the notifications it drives.
 *  `/bookings role=` takes a name OR the `=#id` pin; `/notifications role=` is
 *  name-only (iexact), so the name is required there. */
export function roleActions(role: { id: number; name?: string | null }, ctx: NotificationCtx = {}): ValueAction[] {
  const out: ValueAction[] = [
    act.filter(bookingsHref([idCond('role', role.id)], tenantOf(ctx)), 'Bookings with this role'),
  ];
  if (role.name) {
    out.push(
      act.filter(
        notificationsHref([cond('role', role.name)], tenantOf(ctx)),
        'Notifications triggered by this role',
      ),
      act.copy(role.name, 'Copy role name'),
    );
  }
  return out;
}

/** A tour this notification is limited to. `/delivery-logs tour=` and
 *  `/notifications tour=` are exact ints; `/bookings tour=` takes the `=#id`
 *  pin so same-named tours do not leak in. */
export function tourActions(tour: { id: number }, ctx: NotificationCtx = {}): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.open(`/tours/${tour.id}`, 'Open tour'),
    act.filter(bookingsHref([idCond('tour', tour.id)], t), 'Bookings on this tour'),
    act.filter(
      deliveryLogsHref([cond('tour', String(tour.id), 'exact')], { tenant: t }),
      'Delivery logs for this tour',
    ),
    act.filter(
      notificationsHref([cond('tour', String(tour.id), 'exact')], t),
      'Notifications targeting this tour',
    ),
  ];
}

/** An event code: the notifications configured on it, and what it delivered. */
export function eventActions(code: string, ctx: NotificationCtx = {}): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(code, 'Copy event code'),
    act.filter(notificationsHref([cond('event', code)], t), 'Notifications for this event'),
    act.filter(
      deliveryLogsHref([cond('event', code)], { tenant: t }),
      'Delivery logs for this event (7 days)',
    ),
  ];
}

/** The same event over the timeline's 30-day stats window, so the number on
 *  screen and the grid the link opens are counting the same thing. */
export function eventFiresActions(code: string, ctx: NotificationCtx = {}): ValueAction[] {
  return [
    act.filter(
      deliveryLogsHref([cond('event', code), THIRTY_DAYS()], { tenant: tenantOf(ctx) }),
      'Delivery logs for this event (30 days)',
    ),
  ];
}

// ---- Records --------------------------------------------------------------

/** Another notification (timeline card, anatomy picker, proposal item). */
export function notificationActions(id: number): ValueAction[] {
  return [
    act.open(`/notifications/${id}`, 'Open editor'),
    act.filter(deliveryLogsHref([cond('notification', String(id), 'exact')]), 'Delivery logs (7 days)'),
    act.copy(String(id), 'Copy id'),
  ];
}

/** The 30-day send count on a notification card. */
export function notificationFiresActions(id: number): ValueAction[] {
  return [
    act.filter(
      deliveryLogsHref([cond('notification', String(id), 'exact'), THIRTY_DAYS()]),
      'Delivery logs (30 days)',
    ),
  ];
}

export function rulesetActions(rs: { id: number; codename?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/notifications/rulesets/${rs.id}`, 'Open ruleset')];
  if (rs.codename) out.push(act.copy(rs.codename, 'Copy codename'));
  return out;
}

export function otaActions(ota: { id: number; name?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/otas/${ota.id}`, 'Open OTA')];
  if (ota.name) out.push(act.copy(ota.name, 'Copy name'));
  return out;
}

export function userActions(user: { id: number; email?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/users/${user.id}`, 'Open user')];
  if (user.email) out.push(act.mailto(user.email), act.copy(user.email, 'Copy email'));
  return out;
}

export function bgtaskActions(taskId: string): ValueAction[] {
  return [act.open(`/bgtasks/${taskId}`, 'Open background task'), act.copy(taskId, 'Copy task id')];
}

/** A tag category code, as the rule editors and design-import items carry it
 *  (the category ID is not in those payloads — only the code). */
export function tagCategoryCodeActions(code: string, ctx: NotificationCtx = {}): ValueAction[] {
  return [
    act.filter(tagsHref([cond('category', code)], tenantOf(ctx)), 'Tags in this category'),
    act.filter(bookingsHref([cond('category', code)], tenantOf(ctx)), 'Bookings tagged in this category'),
    act.copy(code, 'Copy code'),
  ];
}

/** A tour tag chip inside a rule (`tours tagged with …`). */
export function tourTagActions(tag: { id: number; name?: string | null }, ctx: NotificationCtx = {}): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/tags/${tag.id}`, 'Open tag'),
    act.filter(toursHref([idCond('tag', tag.id)], tenantOf(ctx)), 'Tours with this tag'),
    act.filter(bookingsHref([idCond('tour_tag', tag.id)], tenantOf(ctx)), 'Bookings on tours with this tag'),
  ];
  if (tag.name) out.push(act.copy(tag.name, 'Copy name'));
  return out;
}

// ---- Delivery methods -----------------------------------------------------

/** A delivery-method row: its theme is the only thing on it with a console
 *  page (`dm.theme` IS the Notification_Theme id). */
export function deliveryMethodActions(
  dm: { id: number; theme?: number | null },
  themeCodename?: string | null,
): ValueAction[] {
  const out: ValueAction[] = [];
  if (dm.theme != null) {
    out.push(...themeActions({ id: dm.theme, codename: themeCodename ?? null }));
  }
  // Draft rows have a negative placeholder id — copying it would be a lie.
  if (dm.id > 0) out.push(act.copy(String(dm.id), 'Copy delivery-method id'));
  return out;
}

// ---- Feature flags --------------------------------------------------------

/** The flag rows that gate one feature. `/notifications/features` is a
 *  QuerySearch grid: a bare `?feature=` is ignored (Phase 0 fixed that). */
export function featureActions(feature: string): ValueAction[] {
  return [
    act.filter(featureFlagsHref([cond('feature', feature)]), 'Feature flags for this feature'),
    act.copy(feature, 'Copy feature codename'),
  ];
}

/** One gate row inside the waterfall — `id=` deep-links that exact row. */
export function featureRowActions(row: { id: number }, feature: string, level: string): ValueAction[] {
  return [
    act.filter(featureFlagsHref([cond('id', String(row.id), 'exact')]), 'Open this flag row'),
    act.filter(
      featureFlagsHref([cond('feature', feature), cond('level', level)]),
      `Flags for this feature at the ${level} level`,
    ),
    act.copy(String(row.id), 'Copy row id'),
  ];
}

// ---- Test-render / trace context sources ----------------------------------

export type ContextSourceKind = 'event_delivery_log' | 'gtu' | 'order' | 'user';

/** The record a test render / trace builds its context from. The delivery-log
 *  id is a UUID, the three entity ids are console pks. Returns `[]` for a value
 *  that cannot address a record, so a half-typed field grows no dead trigger. */
export function contextSourceActions(kind: ContextSourceKind, rawValue: string): ValueAction[] {
  const value = rawValue.trim();
  if (!value) return [];
  if (kind === 'event_delivery_log') {
    return [act.open(`/delivery-logs/${value}`, 'Open the delivery log'), act.copy(value, 'Copy log id')];
  }
  if (!/^\d+$/.test(value)) return [];
  const route =
    kind === 'gtu' ? `/bookings/${value}` : kind === 'order' ? `/orders/${value}` : `/users/${value}`;
  const label =
    kind === 'gtu' ? 'Open the booking' : kind === 'order' ? 'Open the order' : 'Open the user';
  return [act.open(route, label), act.copy(value, 'Copy id')];
}

// ---- Codenames and attributes (find-replace) ------------------------------

/** A codename you might want to grep the whole template corpus for. */
export function codenameSearchActions(codename: string): ValueAction[] {
  return [
    act.filter(findReplaceHref([cond('codename', codename)]), 'Find this codename in templates'),
    act.copy(codename, 'Copy codename'),
  ];
}

/** A tag-category attribute (`{{ tag.<code>.<attr> }}`) the design reads. */
export function attributeActions(attribute: string, ctx: NotificationCtx = {}): ValueAction[] {
  const nodes = [cond('attr', attribute)];
  return [
    act.filter(findReplaceHref(nodes), 'Templates reading this attribute'),
    act.copy(attribute, 'Copy attribute'),
    ...(tenantOf(ctx) == null
      ? []
      : [
          act.filter(
            findReplaceHref([...nodes, cond('tenant', String(tenantOf(ctx)))]),
            "This operator's templates reading it",
          ),
        ]),
  ];
}

// ---- Design imports -------------------------------------------------------

/** Where a proposal item's `target_id` (an UPDATE item) lives in the console.
 *  `null` for kinds with no console page — the caller then renders plain text
 *  (a link that 404s is worse than none). `delivery_method` has no page of its
 *  own and the payload does not name its notification, so it stays null. */
export function proposalTargetRoute(kind: string, targetId: number): string | null {
  switch (kind) {
    case 'notification':
      return `/notifications/${targetId}`;
    case 'block':
      return `/notifications/blocks?edit=${targetId}`;
    case 'theme':
      return `/notifications/themes?edit=${targetId}`;
    case 'tag_category':
      return `/tag-categories/${targetId}`;
    default:
      return null;
  }
}

export function proposalTargetActions(kind: string, targetId: number): ValueAction[] {
  const route = proposalTargetRoute(kind, targetId);
  if (!route) return [act.copy(String(targetId), 'Copy target id')];
  return [act.open(route, `Open the ${kind.replace(/_/g, ' ')} this updates`), act.copy(String(targetId), 'Copy target id')];
}

/** The materials / proposals grids: `/notifications/design-imports` is a
 *  plain-param page (`?tenant=&kind=&deprecated=`). */
export function designImportsHref(params: {
  tenant?: number | null;
  kind?: string | null;
  deprecated?: string | null;
}): string {
  const usp = new URLSearchParams();
  if (params.tenant != null) usp.set('tenant', String(params.tenant));
  if (params.kind) usp.set('kind', params.kind);
  if (params.deprecated) usp.set('deprecated', params.deprecated);
  const qs = usp.toString();
  return qs ? `/notifications/design-imports?${qs}` : '/notifications/design-imports';
}

export function materialKindActions(kind: string): ValueAction[] {
  return [act.filter(designImportsHref({ kind }), `Materials of kind "${kind}"`)];
}

export function materialDeprecatedActions(deprecated: boolean): ValueAction[] {
  return [
    act.filter(
      designImportsHref({ deprecated: deprecated ? 'true' : 'false' }),
      deprecated ? 'Deprecated materials' : 'Live materials',
    ),
  ];
}
